import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './InspectionPhotosSimple.css';
import inspectionService from '../../services/inspectionService';
import PhotoModal from '../PhotoModal';
import Modal from '../Modal';
import { INSPECTION_STATUS_VERDICT, INSPECTION_STATUS, ROLES } from '../../utils/constants';
import { useAuthContext } from '../../context/AuthContext';
import { formatDate } from '../../utils/helper';
import PhotoUploader from '../PhotoUploader.js/index.js';
import PhotoPreview from '../PhotoPreviewModal/PhotoPreview.js';
import { createPreviewHoverHandlers } from '../../utils/previewHover';
import logger from '../../utils/logger';

const { REJECTED, PENDING, APPROVED } = INSPECTION_STATUS_VERDICT;
const { PENDING_USER, PENDING_REVIEW } = INSPECTION_STATUS;
const { INSPECTOR, ADMINISTRATOR, CREATOR, CREATOR_SENIOR } = ROLES;

const InspectionPhotosSimple = ({ inspectionId, inspectionPhotos, onPhotoRejections, inspectionStatus, onPhotoUpdate, onMissingPhotosRequest,
    onAddMissingPhotos,
    onDownloadPhotosZip,
    onDownloadPhoto,
    missingPhotosRequests = [] }) => {
    const [photos, setPhotos] = useState(inspectionPhotos || []);
    const [photosUrls, setPhotosUrls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [photoQuality, setPhotoQuality] = useState('medium'); // low, medium, high
    const hasLoadedRef = useRef(false);
    const [openModal, setOpenModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [pendingRejections, setPendingRejections] = useState([]);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showMissingPhotosModal, setShowMissingPhotosModal] = useState(false);
    const [missingPhotosComments, setMissingPhotosComments] = useState('');
    const [showAddPhotosModal, setShowAddPhotosModal] = useState(false);

    const [previewPhoto, setPreviewPhoto] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [anchorPosition, setAnchorPosition] = useState(null);
    const showPreviewTimeoutRef = useRef(null);
    const hidePreviewTimeoutRef = useRef(null);

    const { currentUser } = useAuthContext();

    const loadPhotos = useCallback(async () => {
        try {
            setLoading(true);
            logger.info('Cargando fotos...', {
                inspectionId: inspectionId,
            }, 'InspectionPhotosSimple');
            setError(null);
            const data = await inspectionService.getInspectionPhotos(inspectionId);
            const photosUrls = data.photos || [];
            setPhotosUrls(photosUrls);
        } catch (error) {
            logger.error('Error cargando fotos:', error, null, 'InspectionPhotosSimple');
            setError(error.message || 'Error al cargar las fotos');
        } finally {
            setLoading(false);
        }
    }, [inspectionId]);

    useEffect(() => {
        if (inspectionId && !hasLoadedRef.current) {
            hasLoadedRef.current = true;
            loadPhotos();
        }
    }, [inspectionId, loadPhotos]);

    const handleDownloadPhotosZip = () => {
        if (onDownloadPhotosZip) {
            onDownloadPhotosZip(inspectionId);
        }
    };

    const hidePreview = useCallback(() => {
        setShowPreview(false);
        setPreviewPhoto(null);
        setAnchorPosition(null);
    }, []);

    const {
        handlePhotoMouseEnter,
        handlePhotoMouseLeave,
        handlePreviewHoverChange,
    } = useMemo(
        () => createPreviewHoverHandlers({
            showTimeoutRef: showPreviewTimeoutRef,
            hideTimeoutRef: hidePreviewTimeoutRef,
            showDelayMs: 250,
            onAnchorSet: setAnchorPosition,
            onShowPreview: (photo) => {
                setPreviewPhoto(photo);
                setShowPreview(true);
            },
            onHide: hidePreview,
        }),
        [hidePreview]
    );

    useEffect(() => {
        return () => {
            if (showPreviewTimeoutRef.current) {
                clearTimeout(showPreviewTimeoutRef.current);
            }
            if (hidePreviewTimeoutRef.current) {
                clearTimeout(hidePreviewTimeoutRef.current);
            }
        };
    }, []);

    const anyRejectedPhotos = photos.some(photo => photo.status === REJECTED);

    const handleRejectPhoto = (photo) => {
        const photoUrlObj = photosUrls.find(p => p.id === photo.id);
        const photoSelected = photoUrlObj ? { ...photo, url: photoUrlObj.url } : photo;
        setSelectedPhoto(photoSelected);
        setShowRejectionModal(true);
    };

    const confirmRejection = (photo, rejectionReason) => {
        const rejection = {
            id: photo.id || photo.key,
            name: photo.name || `Foto ${photos.findIndex(p => p.id === photo.id) + 1}`,
            photoKey: photo.key,
            finishedBy: currentUser.name,
            finishedAt: new Date().toISOString(),
            rejectionReason,
        };

        const updatedPhotos = photos.map(p => p.id === photo.id ? { ...p, status: REJECTED, rejectionReason, finishedAt: new Date().toISOString(), finishedBy: currentUser.name } : p);
        setPhotos(updatedPhotos);

        setPendingRejections(prev => [...prev, rejection]);
        setShowRejectionModal(false);
        setRejectionReason('');
        closeModal();
    };

    const handlePhotoUpdate = async (oldPhoto, newPhotoData) => {
        const photoUpdates = photos.map(photo => {
            return photo.key === oldPhoto.key ? {
                status: PENDING,
                uploadedAt: new Date().toISOString(),
                id: photo.id,
                key: photo.key,
                name: photo.name,
            } : photo;
        });
        const result = await onPhotoUpdate(photoUpdates, { ...newPhotoData, id: selectedPhoto.id });
        if (result?.success) {
            await loadPhotos();
            setOpenModal(false);
            setSelectedPhoto(null);
            setPhotos(result.photos);
        }
    };

    const confirmAllRejections = async () => {
        const photoUpdates = photos.map(photo => {
            const rejection = pendingRejections.find(r => r.id === photo.id);
            if (rejection) {
                return {
                    ...photo,
                    status: REJECTED,
                    rejectionReason: rejection.rejectionReason,
                    finishedAt: rejection.finishedAt,
                    finishedBy: rejection.finishedBy
                };
            }
            return photo;
        });
        setPhotos(photoUpdates);
        await onPhotoRejections(photoUpdates);

        setPendingRejections([]);
        setShowConfirmModal(false);
    };

    const closeModal = () => {
        setSelectedPhoto(null);
        setOpenModal(false);
    };

    const getPhotoUrl = (photo, quality = 'medium') => {
        if (!photo) return '';

        return photosUrls.find(p => p.id === photo.id || p.key === photo.key)?.url || photo.url;
    };

    const hasPendingRejections = pendingRejections.length > 0;

    const getPhotoStatus = (photo) => {

        return photo.status || PENDING;
    };

    const handleRequestMissingPhotos = async () => {
        if (!missingPhotosComments.trim()) return;

        try {
            const newRequest = {
                id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                inspectorName: currentUser.name,
                timestamp: new Date().toISOString(),
                comments: missingPhotosComments.trim(),
                status: PENDING
            };

            await onMissingPhotosRequest(newRequest);

            setMissingPhotosComments('');
            setShowMissingPhotosModal(false);

        } catch (error) {
            logger.error('Error requesting missing photos:', error, null, 'InspectionPhotosSimple');
        }
    };

    const handleAddMissingPhotos = async (selectedPhotos) => {
        setShowAddPhotosModal(true);
    };

    const handleConfirmAddMissingPhotos = async (photosToAdd) => {
        if (photos.length === 0) return;
        const numberCurrentPhotos = photos.length;
        const newPhotosData = photosToAdd.map((photo, index) => ({
            name: `Foto ${numberCurrentPhotos + index + 1}`,
            id: `${inspectionId}_${numberCurrentPhotos + index + 1}.jpg`,
            key: `${inspectionId}/photos/${inspectionId}_${numberCurrentPhotos + index + 1}.jpg`,
            status: PENDING,
            uploadedAt: new Date().toISOString(),
        }));

        const newPhotosFiles = photosToAdd.map((photo, index) => {
            return {
                file: photo.file,
                id: `${inspectionId}_${numberCurrentPhotos + index + 1}.jpg`,
            }
        });
        try {
            const result = await onAddMissingPhotos(newPhotosData, newPhotosFiles);
            setShowAddPhotosModal(false);
            if (result?.success) {
                await loadPhotos();
                setPhotos(result.photos);
            }
        } catch (error) {
            logger.error('Error adding missing photos:', error, null, 'InspectionPhotosSimple');
        }
    };

    const canRequestMissingPhotos = () => {

        return [INSPECTOR, ADMINISTRATOR].includes(currentUser.role) &&
            [PENDING_REVIEW, PENDING_USER].includes(inspectionStatus);
    };

    const canAddMissingPhotos = () => {
        return (currentUser.role === CREATOR || currentUser.role === CREATOR_SENIOR) &&
            inspectionStatus === PENDING_USER &&
            missingPhotosRequests.some(request => request.status === PENDING);
    };

    const hasActiveMissingPhotosRequests = () => {
        return missingPhotosRequests.some(request => request.status === PENDING) &&
            inspectionStatus === PENDING_USER;
    };

    // 🔧 NUEVO: Formatear comentarios de solicitudes
    const formatMissingPhotosComments = () => {
        const pendingRequests = missingPhotosRequests
            .filter(request => request.status === PENDING)
            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

        return pendingRequests.map(request => {
            const formattedDate = formatDate(request.timestamp, 'dd/MM/yyyy HH:mm');
            return `[${formattedDate} - ${request.inspectorName}]: ${request.comments}.`;
        }).join('\n');
    };

    if (loading) {
        return (
            <div className="photos-simple-loading">
                <div className="loading-spinner"></div>
                <p>Cargando fotos de la inspección...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="photos-simple-error">
                <p>Error: {error}</p>
                <button onClick={loadPhotos} className="btn btn-primary">
                    Reintentar
                </button>
            </div>
        );
    }

    if (photos.length === 0) {
        return (
            <div className="photos-simple-empty">
                <p>No hay fotos disponibles para esta inspección</p>
            </div>
        );
    }

    const enableRejectButtons = () => {
        return ![APPROVED, REJECTED].includes(inspectionStatus) && ![REJECTED].includes(selectedPhoto?.status) && [INSPECTOR, ADMINISTRATOR].includes(currentUser.role);
    };

    const handleEnableUpdatePhotoButton = () => {
        return [REJECTED].includes(selectedPhoto?.status) && inspectionStatus === PENDING_USER && (currentUser.role === ADMINISTRATOR || currentUser.role === CREATOR || currentUser.role === CREATOR_SENIOR);
    };

    const isInspectionPendingForUser = inspectionStatus === PENDING_USER && anyRejectedPhotos;

    return (
        <div className="inspection-photos-simple">
            {/* Controles de calidad */}
            <div className="photo-quality-controls">
                <div className="quality-control-left">
                    <label>Calidad de imagen:</label>
                    <select
                        value={photoQuality}
                        onChange={(e) => setPhotoQuality(e.target.value)}
                        className="quality-select"
                    >
                        <option value="low">Baja</option>
                        <option value="medium">Media</option>
                        <option value="high">Alta</option>
                    </select>
                </div>

                <div className="quality-control-right">
                    <button
                        className="btn btn-primary download-zip-btn"
                        onClick={handleDownloadPhotosZip}
                        disabled={photos.length === 0}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                            <polyline points="7,10 12,15 17,10"></polyline>
                            <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        Descargar ZIP
                    </button>
                </div>
            </div>

            {/* Información de fotos */}
            <div className="photos-info">
                <p>Total de fotos: {photos.length}</p>
            </div>

            {/* Grid de fotos */}
            <div className="photos-grid">
                {photos.map((photo, index) => (
                    <div
                        key={photo.id || index}
                        className="photo-item"
                        onClick={() => {
                            const photoUrlObj = photosUrls.find(p => p.id === photo.id || p.key === photo.key);
                            const photoSelected = photoUrlObj ? { ...photo, url: photoUrlObj.url } : photo;
                            setSelectedPhoto(photoSelected);
                            setOpenModal(true);
                        }}
                        onMouseEnter={(e) => handlePhotoMouseEnter(photo, e)}
                        onMouseLeave={handlePhotoMouseLeave}
                    >
                        <div className="photo-container">
                            <img
                                src={getPhotoUrl(photo, photoQuality)}
                                alt={`Foto ${index + 1}`}
                                className="photo-thumbnail"
                                loading="lazy"
                                onError={(e) => {
                                    e.target.src = '/placeholder-image.png';
                                    e.target.alt = 'Error al cargar imagen';
                                }}
                            />

                            {getPhotoStatus(photo) === REJECTED && (
                                <div className="photo-rejection-overlay">
                                    <span className="photo-rejection-label">RECHAZADO</span>
                                </div>
                            )}

                            <div className="photo-overlay">
                                <span className="photo-number">{photo.name}</span>
                            </div>
                        </div>
                    </div>
                ))}

                {/* 🔧 NUEVO: Icono para agregar fotos faltantes */}
                {canAddMissingPhotos() && (
                    <div
                        className="photo-item add-missing-photos-item"
                        onClick={() => handleAddMissingPhotos([])}
                    >
                        <div className="add-missing-photos-container">
                            <div className="add-missing-photos-icon">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M12 5v14"></path>
                                    <path d="M5 12h14"></path>
                                </svg>
                            </div>
                            <span className="add-missing-photos-text">Subir fotos faltantes</span>
                        </div>
                    </div>
                )}
            </div>

            {/* 🔧 NUEVO: Preview de foto */}
            {previewPhoto && (
                <PhotoPreview
                    isVisible={showPreview}
                    photo={{ ...previewPhoto, url: getPhotoUrl(previewPhoto, photoQuality) }}
                    anchorPosition={anchorPosition}
                    onPreviewHoverChange={handlePreviewHoverChange}
                />
            )}

            {/* 🔧 NUEVO: Botón para solicitar fotos faltantes */}
            {canRequestMissingPhotos() && (
                <div className="missing-photos-actions">
                    <button
                        className="btn btn-primary request-missing-photos-btn"
                        onClick={() => setShowMissingPhotosModal(true)}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 12l2 2 4-4"></path>
                            <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"></path>
                        </svg>
                        Solicitar Fotos Faltantes
                    </button>
                </div>
            )}

            {/* 🔧 NUEVO: Comentarios de solicitudes de fotos faltantes */}
            {hasActiveMissingPhotosRequests() && (
                <div className="missing-photos-comments-section">
                    <h4 className="missing-photos-comments-title">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        Solicitudes de Fotos Faltantes
                    </h4>
                    <div className="missing-photos-comments-content">
                        <pre className="missing-photos-comments-text">
                            {formatMissingPhotosComments()}
                        </pre>
                    </div>
                </div>
            )}

            {/* 🔧 Indicador de estado de inspección */}
            {isInspectionPendingForUser && (
                <div className="inspection-status-warning">
                    <div className="warning-icon">⚠️</div>
                    <div className="warning-content">
                        <h4>Fotos Rechazadas</h4>
                        <p>Existen fotos rechazadas en esta inspección. Puedes continuar trabajando en las áreas de <strong>antecedentes</strong> y <strong>dictamen</strong> normalmente.</p>
                        <p>Las fotos pendientes se aprobarán automáticamente al finalizar la inspección. Por favor, ingrese en las imágenes rechazadas para ver el motivo del rechazo y subir una nueva foto si es necesario.</p>
                    </div>
                </div>
            )}

            {/* Modal para foto ampliada */}
            <PhotoModal
                selectedPhoto={selectedPhoto}
                closeModal={closeModal}
                open={openModal}
                enableApproveButtons={() => false}
                enableRejectButtons={enableRejectButtons}
                handleRejectPhoto={handleRejectPhoto}
                updatePhoto={handlePhotoUpdate}
                enableUpdatePhotoButton={handleEnableUpdatePhotoButton}
                onDownloadPhoto={onDownloadPhoto}
            />

            {hasPendingRejections && (
                <div className="confirm-rejections-container">
                    <button
                        className="btn btn-primary confirm-rejections-btn"
                        onClick={() => setShowConfirmModal(true)}
                    >
                        Confirmar {pendingRejections.length} Rechazo{pendingRejections.length > 1 ? 's' : ''} ({pendingRejections.length})
                    </button>
                </div>
            )}

            {/* 🔧 NUEVO: Modal de confirmación */}
            <Modal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                title="Confirmar Rechazos de Fotos"
                size="medium"
            >
                <div className="confirm-rejections-content">
                    <p>¿Estás seguro de que quieres confirmar los siguientes rechazos?</p>

                    <div className="rejections-summary">
                        {pendingRejections.map((rejection, index) => (
                            <div key={index} className="rejection-item">
                                <span className="rejection-photo-name">{rejection.name}</span>
                                <span className="rejection-reason">{rejection.rejectionReason}</span>
                            </div>
                        ))}
                    </div>

                    <p className="confirmation-warning">
                        <strong>⚠️ Importante:</strong> Una vez confirmado, se actualizará la información en la base de datos.
                    </p>
                </div>

                <div className="modal-actions">
                    <button
                        className="btn btn-cancel"
                        onClick={() => setShowConfirmModal(false)}
                    >
                        Cancelar
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={confirmAllRejections}
                    >
                        Confirmar Rechazos
                    </button>
                </div>
            </Modal>

            {/* 🔧 MODIFICADO: Modal de rechazo */}
            <Modal
                isOpen={showRejectionModal}
                onClose={() => {
                    setShowRejectionModal(false);
                    setRejectionReason('');
                }}
                title="Motivo de Rechazo"
                size="small"
            >
                <div className="form-group">
                    <label htmlFor="rejectionReason">
                        Se solicitará que se suba una nueva foto para continuar con la inspección.
                    </label>
                    <textarea
                        id="rejectionReason"
                        placeholder="Ingrese el motivo de rechazo"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows="4"
                    />
                </div>

                <div className="modal-actions">
                    <button
                        className="btn btn-cancel"
                        onClick={() => {
                            setShowRejectionModal(false);
                            setRejectionReason('');
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        className={`btn btn-primary ${rejectionReason.length === 0 ? 'rejection-button-disabled' : ''}`}
                        onClick={() => confirmRejection(selectedPhoto, rejectionReason)}
                        disabled={rejectionReason.length === 0}
                    >
                        Rechazar Foto
                    </button>
                </div>
            </Modal>

            {/* 🔧 NUEVO: Modal para solicitar fotos faltantes */}
            <Modal
                isOpen={showMissingPhotosModal}
                onClose={() => {
                    setShowMissingPhotosModal(false);
                    setMissingPhotosComments('');
                }}
                title="Solicitar Fotos Faltantes"
                size="medium"
                showCloseButton={true}
                closeOnBackdropClick={true}
            >
                <div className="missing-photos-modal-content">
                    <div className="missing-photos-description">
                        <p>Esta solicitud quedará registrada para que el usuario que creó la inspección agregue las fotos que faltan.</p>
                        <p>Una vez que se agreguen las fotos, el proceso de revisión se reiniciará.</p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="missingPhotosComments">
                            Comentarios sobre las fotos faltantes: *
                        </label>
                        <textarea
                            id="missingPhotosComments"
                            placeholder="Describe qué fotos faltan o qué necesitas que se agregue..."
                            value={missingPhotosComments}
                            onChange={(e) => setMissingPhotosComments(e.target.value)}
                            rows="4"
                            className="missing-photos-textarea"
                        />
                    </div>

                    <div className="modal-actions">
                        <button
                            className={`btn btn-primary ${!missingPhotosComments.trim() ? 'disabled' : ''}`}
                            onClick={handleRequestMissingPhotos}
                            disabled={!missingPhotosComments.trim()}
                        >
                            Enviar Solicitud
                        </button>
                    </div>
                </div>
            </Modal>

            {/* 🔧 NUEVO: Modal para agregar fotos faltantes */}
            <Modal
                isOpen={showAddPhotosModal}
                onClose={() => {
                    setShowAddPhotosModal(false);
                }}
                title="Agregar Fotos Faltantes"
                size="large"
                showCloseButton={true}
                closeOnBackdropClick={true}
            >
                <div className="add-missing-photos-modal-content">
                    <PhotoUploader
                        maxPhotos={10}
                        onPhotosSelected={(newPhotos) => handleConfirmAddMissingPhotos(newPhotos)}
                        onCancel={() => {
                            setShowAddPhotosModal(false);
                        }}
                        title="Seleccionar Fotos Faltantes"
                        description="Selecciona las fotos que faltan según la solicitud del inspector"
                        acceptMultiple={true}
                        showPreview={true}
                        allowRemove={true}
                        requireConfirmation={false}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default InspectionPhotosSimple;